import { FC, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import './ValidateSessionInfoContainer.scss';
import useUrlParams from '../../hooks/useUrlParams';
import useSatellite from '../../hooks/useSatellite';
import useScenario from '../../hooks/useScenario';
import { getSetupsBySatellite, getGroundStationProviders } from '../../../validate/api/validate.api';
import { ACP_SATOS_VERSION } from '../../../../release-version';
import type { ValidateSetup, ValidateSession } from '../../../validate/types/validateDetails.types';
import { CloseIcon } from './Svgs';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  close: (e: MouseEvent<HTMLButtonElement>) => void;
}

// ─── Row helper ───────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="vsi__row">
      <span className="vsi__row__label">{label}</span>
      <span className="vsi__row__value">{value || '—'}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const ValidateSessionInfoContainer: FC<Props> = ({ open, close }) => {
  const { mission_id, sat_grp_id, sat_id, scenario_id } = useUrlParams();
  const { selected_satellite } = useSatellite();
  const { scenario } = useScenario();

  const [matchedSetup,   setMatchedSetup]   = useState<ValidateSetup   | null>(null);
  const [matchedSession, setMatchedSession] = useState<ValidateSession  | null>(null);
  const [gspName,        setGspName]        = useState<string>('');

  // Fetch setups → find the setup containing the current session (scenario_id)
  useEffect(() => {
    if (!mission_id || !sat_grp_id || !sat_id) return;
    let cancelled = false;

    getSetupsBySatellite(mission_id, sat_grp_id, sat_id).then(({ data }) => {
      if (cancelled) return;

      let foundSetup: ValidateSetup | null = null;
      let foundSession: ValidateSession | null = null;

      if (scenario_id) {
        for (const setup of data) {
          const session = setup.sessions.find((s) => s.sessionId === scenario_id);
          if (session) { foundSetup = setup; foundSession = session; break; }
        }
      }

      // Fallback: first setup with a running session, or just first setup
      if (!foundSetup) {
        foundSetup = data.find((s) => s.sessions.some((sess) => sess.status === 'RUNNING')) ?? data[0] ?? null;
        foundSession = foundSetup?.sessions.find((s) => s.status === 'RUNNING') ?? foundSetup?.sessions[0] ?? null;
      }

      setMatchedSetup(foundSetup);
      setMatchedSession(foundSession);
    });

    return () => { cancelled = true; };
  }, [mission_id, sat_grp_id, scenario_id]);

  // Fetch provider name once we have the setup's groundStationProviderId
  useEffect(() => {
    const satIdForProviders = sat_id || sat_grp_id;
    const providerId = matchedSetup?.groundStationProviderId;
    if (!satIdForProviders || !providerId) return;
    let cancelled = false;

    getGroundStationProviders(satIdForProviders).then(({ data }) => {
      if (cancelled) return;
      const match = data.find((p) => p.value === providerId);
      setGspName(match?.label ?? providerId);
    });

    return () => { cancelled = true; };
  }, [sat_id, sat_grp_id, matchedSetup?.groundStationProviderId]);

  const modelName  = selected_satellite?.label ?? selected_satellite?.value?.satelliteName;
  const modelType  = matchedSetup?.modelType;
  const modelMode  = matchedSetup?.modelMode;
  const testingEnv = matchedSession?.testingEnvironment;
  const satos      = ACP_SATOS_VERSION;

  return (
    <div
      className={`vsi__container${open ? ' vsi__container--active' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div className="vsi__header">
        <span className="vsi__title">Session Setup</span>
        <button className="vsi__close" onClick={close} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      {/* ── Rows ── */}
      <div className="vsi__rows">
        <InfoRow label="Model Name"  value={modelName}  />
        <InfoRow label="Model Type"  value={modelType}  />
        <InfoRow label="Model Mode"  value={modelMode}  />
        <InfoRow label="GSP"         value={gspName}    />
        <InfoRow label="Satos"       value={satos}      />
        <InfoRow label="Testing ENV" value={testingEnv} />
      </div>
    </div>
  );
};

export default ValidateSessionInfoContainer;
